import React, { useState } from 'react';

const ALWAYS_EXPAND = ['user_data', 'variables', '__output__'];
const MAX_STRING_PREVIEW = 120;

/** console-ui's default fold: noisy or deep branches start closed, the useful ones open. */
export function startsCollapsed(key: string | number | undefined, value: object, depth: number) {
  if (typeof key === 'string' && ALWAYS_EXPAND.includes(key)) return false;
  if (typeof key === 'string' && key.startsWith('__')) return true;
  const count = Array.isArray(value) ? value.length : Object.keys(value).length;
  return (Array.isArray(value) ? count > 2 : count > 4) || depth > 1;
}

const Key: React.FC<{ name?: string | number }> = ({ name }) =>
  name === undefined ? null : <span className="text-primary">{String(name)}: </span>;

const Leaf: React.FC<{ name?: string | number; value: unknown }> = ({ name, value }) => {
  const [full, setFull] = useState(false);
  if (typeof value === 'string' && value.length > MAX_STRING_PREVIEW) {
    return (
      <div className="pl-3">
        <Key name={name} />
        <button
          type="button"
          className="text-left text-positive-foreground hover:underline"
          onClick={() => setFull(v => !v)}
        >
          &quot;{value.slice(0, MAX_STRING_PREVIEW)}…&quot; ({value.length} chars)
        </button>
        {full && <pre className="whitespace-pre-wrap break-words pl-3">{value}</pre>}
      </div>
    );
  }
  const className =
    value === null || value === undefined
      ? 'italic text-muted-foreground'
      : typeof value === 'string'
        ? 'text-positive-foreground'
        : typeof value === 'boolean'
          ? 'text-destructive'
          : 'text-attention-foreground';
  return (
    <div className="pl-3">
      <Key name={name} />
      <span className={className}>{value === undefined ? 'null' : JSON.stringify(value)}</span>
    </div>
  );
};

const Node: React.FC<{ name?: string | number; value: unknown; depth: number }> = ({
  name,
  value,
  depth,
}) => {
  if (value === null || typeof value !== 'object') return <Leaf name={name} value={value} />;
  const isArray = Array.isArray(value);
  const entries: [string | number, unknown][] = isArray
    ? (value as unknown[]).map((v, i) => [i, v])
    : Object.entries(value as Record<string, unknown>);
  return (
    <details className="pl-3" open={!startsCollapsed(name, value, depth)}>
      <summary className="cursor-pointer select-none">
        <Key name={name} />
        <span className="text-muted-foreground">
          {isArray ? `Array(${entries.length})` : `{${entries.length} keys}`}
        </span>
      </summary>
      {entries.map(([k, v]) => (
        <Node key={String(k)} name={k} value={v} depth={depth + 1} />
      ))}
    </details>
  );
};

/** Collapsible JSON view matching console-ui's side panels; a top-level object lists its keys. */
export const JsonTree: React.FC<{ value: unknown }> = ({ value }) => (
  <div className="-ml-3 font-mono text-xs">
    {value !== null && typeof value === 'object' && !Array.isArray(value) ? (
      Object.entries(value as Record<string, unknown>).map(([k, v]) => (
        <Node key={k} name={k} value={v} depth={0} />
      ))
    ) : (
      <Node value={value} depth={0} />
    )}
  </div>
);
